/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  EMAProviderOrder,
  EMAProviderOrder$inboundSchema,
} from "./emaproviderorder.js";
import {
  EMAServiceServer,
  EMAServiceServer$inboundSchema,
} from "./emaserviceserver.js";

/**
 * One service identity chaining reaches in the organization: a vendor authorization server, the identity providers that can vouch for people to it with whether the vendor has accepted each, and the MCP servers that chain through it.
 */
export type EMAService = {
  /**
   * The EMA providers one vendor authorization server accepts, saved or default. They are stored as an ordered list that identity chaining tries in turn, but each endpoint can use only the provider its sign-in goes through, so the list acts as the set of accepted providers.
   */
  emaProviders: EMAProviderOrder;
  /**
   * Counted servers the caller cannot read; they are not named.
   */
  hiddenServerCount: number;
  /**
   * The vendor's display name, from an issuer record that names it. Empty when none has one.
   */
  name: string;
  /**
   * Of those, how many have a ready binding, including servers the caller cannot read.
   */
  readyServerCount: number;
  /**
   * An issuer record the organization can name for this vendor, organization-level first, to pass to setEMAProviders. Omitted only when no issuer record names the vendor any more.
   */
  remoteSessionIssuerId?: string | undefined;
  /**
   * Distinct live MCP servers whose identity chaining binding for this vendor has been turned on, in any state after setup, including servers the caller cannot read.
   */
  serverCount: number;
  /**
   * The counted servers the caller can read (mcp:read), ordered by project and name.
   */
  servers: Array<EMAServiceServer>;
  /**
   * The vendor authorization server issuer, canonicalized.
   */
  vendorIssuer: string;
};

/** @internal */
export const EMAService$inboundSchema: z.ZodMiniType<EMAService, unknown> = z
  .pipe(
    z.object({
      ema_providers: EMAProviderOrder$inboundSchema,
      hidden_server_count: z.int(),
      name: z.string(),
      ready_server_count: z.int(),
      remote_session_issuer_id: z.optional(z.string()),
      server_count: z.int(),
      servers: z.array(EMAServiceServer$inboundSchema),
      vendor_issuer: z.string(),
    }),
    z.transform((v) => {
      return remap$(v, {
        "ema_providers": "emaProviders",
        "hidden_server_count": "hiddenServerCount",
        "ready_server_count": "readyServerCount",
        "remote_session_issuer_id": "remoteSessionIssuerId",
        "server_count": "serverCount",
        "vendor_issuer": "vendorIssuer",
      });
    }),
  );

export function emaServiceFromJSON(
  jsonString: string,
): SafeParseResult<EMAService, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => EMAService$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'EMAService' from JSON`,
  );
}
